import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Sidebar from '@/components/Sidebar';
import MobileNav from '@/components/MobileNav';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, DollarSign, Download, Clock, CheckCircle2, XCircle } from 'lucide-react';
import { getBudgets, downloadBudgetPdf } from '@/lib/api';
import { toast } from 'sonner';

const Budgets = ({ user }) => {
  const [budgets, setBudgets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBudgets();
  }, []);

  const loadBudgets = async () => {
    try {
      const response = await getBudgets();
      setBudgets(response.data);
    } catch (error) {
      toast.error('Failed to load budgets');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPdf = async (budget) => {
    try {
      const response = await downloadBudgetPdf(budget.budget_id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Budget_${budget.event_name.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">
            <CheckCircle2 className="h-3 w-3 mr-1" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-100 text-red-800 border-red-200">
            <XCircle className="h-3 w-3 mr-1" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge className="bg-amber-100 text-amber-800 border-amber-200">
            <Clock className="h-3 w-3 mr-1" />
            Pending
          </Badge>
        );
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      <Sidebar user={user} />
      
      <div className="flex-1 flex flex-col overflow-hidden md:ml-64">
        <header className="bg-white border-b border-border px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground" style={{ fontFamily: 'Manrope, sans-serif' }}>Budgets</h2>
              <p className="text-sm text-muted-foreground">View and manage event budgets</p>
            </div>
            <Link to="/budgets/new">
              <Button data-testid="create-budget-button">
                <Plus className="h-4 w-4 mr-2" />
                New Budget
              </Button>
            </Link>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-6 pb-24 md:pb-6">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            </div>
          ) : budgets.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <DollarSign className="h-16 w-16 text-muted-foreground mb-4" />
              <h3 className="text-xl font-semibold text-foreground mb-2">No budgets yet</h3>
              <p className="text-muted-foreground mb-6">Create your first budget for an event</p>
              <Link to="/budgets/new">
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Budget
                </Button>
              </Link>
            </div>
          ) : (
            <div className="max-w-4xl mx-auto space-y-4">
              {budgets.map((budget) => (
                <Card key={budget.budget_id} className="card-hover" data-testid={`budget-card-${budget.budget_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-foreground">{budget.event_name}</h3>
                          {getStatusBadge(budget.status)}
                        </div>
                        
                        <p className="text-2xl font-bold text-primary mb-3">
                          ₹{budget.total_amount?.toLocaleString('en-IN') || 0}
                        </p>
                        
                        <div className="flex flex-wrap gap-2 mb-3">
                          {budget.categories?.slice(0, 4).map((cat, idx) => (
                            <span key={idx} className="text-xs px-2 py-1 bg-accent/50 rounded-full">
                              {cat.name}: ₹{cat.amount?.toLocaleString()}
                            </span>
                          ))}
                          {budget.categories?.length > 4 && (
                            <span className="text-xs px-2 py-1 bg-accent/50 rounded-full">
                              +{budget.categories.length - 4} more
                            </span>
                          )}
                        </div>
                        
                        <p className="text-sm text-muted-foreground">
                          Submitted by {budget.submitted_by_name}
                        </p>
                      </div>
                      
                      <Button
                        onClick={() => handleDownloadPdf(budget)}
                        variant="outline"
                        data-testid={`download-budget-pdf-${budget.budget_id}`}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </main>

        <MobileNav user={user} />
      </div>
    </div>
  );
};

export default Budgets;
